import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest, formatDate } from '../../utils';
import './MemberHome.css';

/* "Invite people to an event" — pick one, tick the colleagues, send.

   TWO kinds of event live in the one picker, because an admin arrives wanting to invite
   people and does not think of them as two jobs: the Bravos Labs the company bought seats
   to, and the events the company runs itself. They are grouped, and only when both exist —
   a company with Labs alone should not grow a heading over a list it already understood.

   What separates them is what the invitation IS. A Lab invite carries a signed link that
   provisions an account for whoever opens it and spends a pool seat when they accept; our
   own event is private (its page needs a login and only the company's own people may
   register), so its people are invited from the roster, nothing is spent, and there is no
   link to hand out and no guest to type in. So for our own events the seat line, the guest
   card and the copy-link button are simply not there.

   This replaces the copy-link button that used to sit on every row of the admin's calendar. That
   button asked someone filling a Lab with six people to do it one clipboard paste at a time, and
   it hid the only question that matters: WHO is coming. Here the roster is the screen.

   Picking people spends nothing. The seat leaves the company's pool when an invitee ACCEPTS, so
   the open count is a budget, not a reservation — which is why it is shown next to the send
   button rather than deducted as you tick.

   Reached from the calendar card on the admin home, and from a Lab's own public page (which
   arrives with that Lab already chosen). Backend: GET/POST /api/bravos/lab-invite. */

/* Reading a pasted-together list of people out of a CSV.

   Deliberately parsed IN THE BROWSER and never uploaded: the file is only ever used to decide
   which rows on screen get ticked, so there is no reason for the list to reach a server, and no
   import job, no staging table and no cleanup to go with it.

   Tolerant on purpose — this file comes out of Excel, Outlook or an HR export, not out of a
   spec. Any delimiter (comma, semicolon, tab), quotes stripped, a header row used when it names
   its columns and ignored when it does not, and everything that is not an email address simply
   skipped. One address per line is a perfectly good file. */
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

const splitCells = (line) => line.split(/[,;\t]/).map(
  (cell) => cell.trim().replace(/^"(.*)"$/, '$1').trim());

const HEADER_FIRST = ['first name', 'firstname', 'voornaam', 'prénom', 'prenom'];
const HEADER_LAST = ['last name', 'lastname', 'name', 'naam', 'surname', 'nom'];

const parsePeopleCsv = (text) => {
  const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  // A header only counts as one when it carries no email address — otherwise the first person
  // in the file would be swallowed as a heading.
  const head = splitCells(lines[0]);
  const hasHeader = !head.some((c) => EMAIL_RE.test(c.toLowerCase()));
  const idx = { first: -1, last: -1 };
  if (hasHeader) {
    head.forEach((c, i) => {
      const key = c.toLowerCase();
      if (idx.first < 0 && HEADER_FIRST.includes(key)) idx.first = i;
      else if (idx.last < 0 && HEADER_LAST.includes(key)) idx.last = i;
    });
  }
  const out = [];
  const seen = new Set();
  lines.slice(hasHeader ? 1 : 0).forEach((line) => {
    const cells = splitCells(line);
    const email = (cells.find((c) => EMAIL_RE.test(c.toLowerCase())) || '').toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    out.push({
      email,
      first_name: idx.first >= 0 ? (cells[idx.first] || '') : '',
      last_name: idx.last >= 0 ? (cells[idx.last] || '') : '',
    });
  });
  return out;
};

/* One row of the picker, drawn the same whichever list it came from. */
const eventOption = (l) => (
  <option key={l.id} value={l.id}>
    {l.name}{l.date ? ` · ${formatDate(l.date)}` : ''}
  </option>
);

const LabInvite = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // The Lab can arrive two ways: in the path (the weekly digest links straight to one) or as
  // router state (the Lab's own page, which already has it in hand). Router state cannot travel
  // in a mail, and a query param would be stripped by usePersistUrlParams — hence the path.
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [labId, setLabId] = useState(
    eventId || (location.state && location.state.eventId) || '');
  const [code, setCode] = useState('');
  const [picked, setPicked] = useState([]);
  const [filters, setFilters] = useState([]);
  const [guest, setGuest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [copied, setCopied] = useState(false);
  // What the last uploaded CSV did: how many it ticked, how many were already in the Lab, and
  // the addresses that are on nobody's roster (offered as guests, never silently dropped).
  const [csv, setCsv] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/lab-invite')
      .then((res) => {
        setData(res);
        setLabId((current) => {
          // Labs first, so the default pick stays the seat the company paid for.
          const all = [...(res.labs || []), ...(res.own_events || [])];
          // Ids come back from SQL Server upper-cased; one pasted out of a mail may not be.
          const wanted = all.find(
            (l) => l.id.toLowerCase() === String(current || '').toLowerCase());
          if (wanted) return wanted.id;
          return all.length ? all[0].id : '';
        });
      })
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const labs = (data && data.labs) || [];
  const ownEvents = (data && data.own_events) || [];
  const lab = labs.find((l) => l.id === labId) || null;
  const own = ownEvents.find((l) => l.id === labId) || null;
  // Whichever kind is picked — what the roster card and the send button hang off.
  const chosen = lab || own;
  const invites = (lab && lab.invites) || [];
  // One seat type is the normal case; the pool only splits when a Lab also offers something
  // like an Off record seat, and then the admin has to say which one they are spending.
  const invite = invites.find((iv) => iv.code === code) || invites[0] || null;

  const members = useMemo(() => {
    const rows = (data && data.members) || [];
    return rows.filter((m) => !filters.length || filters.some((f) => m.tags.includes(f)));
  }, [data, filters]);

  // "Already in" is the same question asked of two different records: a seat on a Lab, an
  // answer on our own event.
  const registered = new Set((own && own.registered) || []);
  const alreadyIn = (m) => (own
    ? registered.has(String(m.id).toLowerCase())
    : !!(labId && (m.attending || []).includes(labId.toLowerCase())));
  // A staged row is somebody uploaded from a CSV who has no account yet. A Lab invite makes them
  // one on the way in; our own event cannot — its page needs a login — so they keep their
  // "not joined yet" pill and stay unpickable rather than being mailed at a locked door.
  const blocked = (m) => alreadyIn(m) || (!!own && m.staged);
  const selectable = members.filter((m) => !blocked(m));
  // Nothing is spent on our own event, so there is no seat to have chosen.
  const canSend = own ? true : !!invite;

  const toggle = (id) => setPicked((p) => (
    p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleFilter = (tag) => setFilters((f) => (
    f.includes(tag) ? f.filter((x) => x !== tag) : [...f, tag]));

  const send = (body, done) => {
    setBusy(true);
    setNotice(null);
    // Two routes because they are two different invitations — see the note at the top.
    const req = own
      ? apiRequest('POST', `/company/event/${labId}/invite`, { ids: body.ids || [] })
      : apiRequest('POST', '/bravos/lab-invite',
                   { event_id: labId, code: invite && invite.code, ...body });
    req
      .then((r) => { setNotice(t('invitesSent', { n: (r && r.sent) || 0 })); done && done(); load(); })
      .catch((err) => setNotice(apiErrorText(t, err, 'somethingWentWrong')))
      .finally(() => setBusy(false));
  };

  /* Tick everyone in an uploaded list.
     ★ It SELECTS rather than sends. Ticking is what the screen already does, so the file lands
     the admin exactly where the manual path lands them — able to see who was matched, uncheck a
     mistake, and press send themselves. A CSV that sent straight away would be one mis-picked
     file away from mailing the wrong hundred people.
     Rows already in this Lab are counted and left alone; addresses on nobody's roster are kept
     aside and offered as guests rather than silently dropped. */
  const loadCsv = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parsePeopleCsv(reader.result);
      const all = (data && data.members) || [];
      const byEmail = new Map(
        all.filter((m) => m.email).map((m) => [m.email.toLowerCase(), m]));
      const matched = [];
      const already = [];
      const unknown = [];
      rows.forEach((row) => {
        const m = byEmail.get(row.email);
        if (!m) unknown.push(row);
        else if (alreadyIn(m)) already.push(m);
        else matched.push(m.id);
      });
      // Added to what is ticked, not replacing it — two files in a row should add up, which is
      // how a per-office export gets used.
      setPicked((p) => Array.from(new Set([...p, ...matched])));
      setCsv({ read: rows.length, matched: matched.length,
               already: already.length, unknown });
      setNotice(null);
    };
    reader.readAsText(file);
  };

  const copyLink = () => {
    if (!invite) return;
    const text = `${t('bravosInviteShareText', { event: lab.name })}\n${invite.link}`;
    const finish = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(finish).catch(finish);
    } else {
      finish();
    }
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="user-add" theme="primary" className="me-2" />
          {t('inviteToLab')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : !labs.length && !ownEvents.length ? (
          <p className="text-muted mb-0">{t('noSeatsToOffer')}</p>
        ) : (
          <>
            <p className="bravos-form__label">{t('chooseLab')}</p>
            <select
              className="bravos-jname"
              value={labId}
              onChange={(e) => { setLabId(e.target.value); setCode(''); setPicked([]); }}
            >
              {/* Grouped only when there is something to tell apart. */}
              {labs.length > 0 && ownEvents.length > 0 ? (
                <>
                  <optgroup label={t('bravosLabsGroup')}>{labs.map(eventOption)}</optgroup>
                  <optgroup label={t('ourOwnEventsGroup')}>
                    {ownEvents.map(eventOption)}
                  </optgroup>
                </>
              ) : [...labs, ...ownEvents].map(eventOption)}
            </select>

            {own && <p className="bravos-q__hint mt-2">{t('ownEventInviteNote')}</p>}

            {invites.length > 1 && (
              <>
                <p className="bravos-form__label mt-2">{t('whichSeat')}</p>
                <select className="bravos-jname" value={invite ? invite.code : ''}
                        onChange={(e) => setCode(e.target.value)}>
                  {invites.map((iv) => (
                    <option key={iv.code} value={iv.code}>
                      {iv.emoji ? `${iv.emoji} ` : ''}{iv.name} · {t('openSeatsN', { n: iv.open })}
                    </option>
                  ))}
                </select>
              </>
            )}

            {invite && (
              <p className="bravos-q__hint mt-2">
                {t('seatsLeftToGive', { n: invite.open })}
              </p>
            )}
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      {data && (data.labs_without_seats || []).length > 0 && (
        <Card>
          <p className="bravos-q__hint mb-1">{t('labsWithoutSeats')}</p>
          {data.labs_without_seats.map((l) => (
            <div key={l.id} className="bravos-member__alloc">
              <span className="bravos-member__alloc-name">
                {l.name}{l.date ? ` · ${formatDate(l.date)}` : ''}
              </span>
            </div>
          ))}
        </Card>
      )}

      {chosen && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="users" theme="primary" className="me-2" />
            {t('whoIsComing')}
          </Title>

          {(data.all_tags || []).length > 0 && (
            <div className="bravos-tags mb-2">
              {data.all_tags.map((tag) => (
                <span
                  key={tag}
                  role="button"
                  tabIndex={0}
                  className={`bravos-tag bravos-tag--filter ${filters.includes(tag) ? 'bravos-tag--on' : ''}`}
                  onClick={() => toggleFilter(tag)}
                  onKeyDown={(e) => { if (e.key === 'Enter') toggleFilter(tag); }}
                >
                  {tag}
                </span>
              ))}
              {filters.length > 0 && (
                <button type="button" className="bravos-tag__add" onClick={() => setFilters([])}>
                  {t('clearFilter')}
                </button>
              )}
            </div>
          )}

          {/* Upload a list instead of ticking a hundred boxes. For a big company this is the
              difference between a minute and an afternoon. */}
          <label className="bravos-form__label mt-2">
            {t('pickFromCsv')}
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { loadCsv(e.target.files[0]); e.target.value = ''; }}
            />
          </label>
          <p className="bravos-q__hint">{t('pickFromCsvHint')}</p>

          {csv && (
            <div className="bravos-recorded mt-1" style={{ display: 'block' }}>
              <p className="mb-1">
                {t('csvMatched', { n: csv.matched, read: csv.read })}
                {csv.already > 0 && ` ${t('csvAlreadyIn', { n: csv.already })}`}
              </p>
              {csv.unknown.length > 0 && (
                <>
                  <p className="mb-1">{t('csvUnknown', { n: csv.unknown.length })}</p>
                  {/* Offered as guests on a Lab, never on our own event: that page is private
                      and an outsider has nothing to open. */}
                  {!own && (
                    <button
                      type="button"
                      className="bravos-copy"
                      disabled={busy || !invite}
                      onClick={() => send({ guests: csv.unknown },
                                          () => setCsv({ ...csv, unknown: [] }))}
                    >
                      <Icon name="paper-plane" />
                      {t('csvInviteUnknown', { n: csv.unknown.length })}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {selectable.length > 0 && (
            <label className="bravos-netbatch__all">
              <input
                type="checkbox"
                checked={picked.length === selectable.length}
                onChange={(e) => setPicked(e.target.checked ? selectable.map((m) => m.id) : [])}
              />
              <span>{t('selectAll')}</span>
            </label>
          )}

          {members.length === 0 ? (
            <p className="text-muted mb-0">{t('noMembersYet')}</p>
          ) : members.map((m) => {
            const inLab = alreadyIn(m);
            return (
              <div key={m.id} className="bravos-member bravos-netpick">
                <div className="bravos-member__row">
                  <input
                    type="checkbox"
                    className="bravos-netpick__cb"
                    checked={picked.includes(m.id)}
                    disabled={blocked(m)}
                    onChange={() => toggle(m.id)}
                  />
                  <span className="bravos-member__body">
                    <span className="bravos-member__name">{m.name}</span>
                    <span className="bravos-member__email">{m.email}</span>
                  </span>
                  {inLab && (
                    <span className="bravos-cta__tag bravos-cta__tag--done">
                      {t('attendingBadge')}
                    </span>
                  )}
                  {m.staged && !inLab && (
                    <span className="bravos-member__pending">{t('notJoinedYet')}</span>
                  )}
                </div>
                {m.tags.length > 0 && (
                  <div className="bravos-tags">
                    {m.tags.map((tag) => (
                      <span key={tag} className="bravos-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className="bravos-submit mt-2"
            disabled={busy || !picked.length || !canSend}
            onClick={() => send({ ids: picked }, () => setPicked([]))}
          >
            {busy ? t('pleaseWait')
              : <><Icon name="paper-plane" /> {t('inviteSelected', { n: picked.length })}</>}
          </button>
        </Card>
      )}

      {lab && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="paper-plane" theme="primary" className="me-2" />
            {t('inviteSomeoneElse')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('inviteSomeoneElseNote')}</p>

          {guest ? (
            <div className="bravos-form">
              <div className="bravos-form__row">
                <input type="text" value={guest.first_name} placeholder={t('firstName')}
                       onChange={(e) => setGuest({ ...guest, first_name: e.target.value })} />
                <input type="text" value={guest.last_name} placeholder={t('lastName')}
                       onChange={(e) => setGuest({ ...guest, last_name: e.target.value })} />
              </div>
              <input type="email" value={guest.email} placeholder={t('enterEmail')}
                     onChange={(e) => setGuest({ ...guest, email: e.target.value })} />
              <div className="bravos-form__row">
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy || !guest.email.trim()}
                  onClick={() => send({ guests: [guest] }, () => setGuest(null))}
                >
                  {busy ? t('pleaseWait') : t('sendInviteEmail')}
                </button>
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={() => setGuest(null)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => setGuest({ first_name: '', last_name: '', email: '' })}
            >
              <Icon name="envelope" /> {t('sendInviteEmail')}
            </button>
          )}

          <button type="button" className="bravos-submit bravos-submit--ghost mt-2"
                  onClick={copyLink} disabled={!invite}>
            <Icon name={copied ? 'check' : 'link'} />{' '}
            {copied ? t('copied') : t('sendInviteLink')}
          </button>
        </Card>
      )}
    </Base>
  );
};

export default LabInvite;
